<template>
  <b-modal
    id="msk-promotion-modal"
    v-model="showModal"
    :title="$t('redeem.voucher.infotextHeading')"
    header-class="underlined"
    hide-footer
    centered
  >
    <div class="mb-2">
      <!-- STEP 1 -->
      <template v-if="stepOne">
        <b-row>
          <b-col cols="12">
            <div class="bg-gray bg-light p-lg-4 p-md-3 p-2">
              <i18n
                tag="h5"
                path="redeem.voucher.codeCorrectValue"
                class="text-center font-weight-bold voucher-value-text my-2"
              >
                <template #price>
                  <span class="price">{{ price }}</span>
                </template>
              </i18n>
            </div>
          </b-col>
          <b-col cols="12">
            <h6 class="mt-lg-5 mb-lg-4 mt-md-4 mb-md-3 mt-3 mb-2 text-center font-weight-bold">
              {{ $t('redeem.voucher.twoOptions') }}:
            </h6>
          </b-col>
        </b-row>

        <!-- Panels -->
        <b-row>
          <!-- Panel left -->
          <b-col md="6" sm="12" order="1" order-md="0">
            <!-- Shopping Account -->
            <b-container fluid class="h-100 py-2">
              <b-row class="d-flex align-content-between justify-content-between h-100 border pb-3">
                <b-col sm="12">
                  <h4 class="text-center mb-2">
                    {{ $t('redeem.msk.putInAccount') }}
                  </h4>
                </b-col>
                <b-col sm="12" class="text-center my-3">
                  <ImageVector
                    :filename="logoMskModal"
                    :fallback="logoMskModalFallback"
                    :alt="$t('redeem.msk.logo')"
                    class="msk-logo"
                  />
                </b-col>
                <b-col sm="12">
                  <span>{{ $t('redeem.msk.yourAdavantages') }}:</span>
                  <ul class="wg-icon-ul">
                    <li><b-icon-check />{{ $t('redeem.voucher.splitVoucher') }}</li>
                    <li><b-icon-check />{{ $t('redeem.msk.allPartnersAvailable', { numberOfShops }) }}</li>
                    <li><b-icon-check />{{ $t('redeem.msk.saveBalanceForYears') }}</li>
                  </ul>
                </b-col>
                <b-col sm="12" class="my-2">
                  <b-button variant="outline-primary" block class="uppercase bold" @click="step = 2">
                    {{ $t('redeem.msk.moreInformation') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-container>
          </b-col>

          <!-- Panel right -->
          <b-col md="6" sm="12">
            <!-- Choose Partner -->
            <b-container fluid class="h-100 py-2">
              <b-row class="d-flex align-content-between justify-content-between h-100 border pb-3">
                <b-col sm="12">
                  <h4 class="text-center">
                    {{ $t('redeem.voucher.redeemAtPartner') }}
                  </h4>
                </b-col>
                <b-col sm="12" class="text-center mb-3">
                  <b-img
                    :src="logoGiftCard"
                    :alt="$t('redeem.msk.logo')"
                    style="width: 330px; max-width: 80%;"
                  />
                </b-col>
                <b-col cols="12" class="p-2 flex-fill" />
                <b-col sm="12" class="my-2">
                  <b-button variant="outline-primary" block class="uppercase bold" @click="$bvModal.hide('msk-promotion-modal')">
                    {{ $t('redeem.voucher.continueToPartners') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-container>
          </b-col>
        </b-row>
      </template>

      <!-- STEP 2 -->
      <div v-else-if="stepTwo" class="p-lg-3 p-md-2 p-sm-1">
        <b-row>
          <b-col cols="12">
            <div class="bg-gray bg-light p-lg-4 p-md-3 p-2">
              <h4 class="text-center font-weight-bold voucher-value-text my-2">
                {{ $t('redeem.msk.sloganWunschgutschein') }}
              </h4>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-4 mb-2">
          <b-col md="6" sm="12">
            <b-col sm="12" class="text-center text-uppercase font-weight-bold now-new">
              {{ $t('wgs.std.nowNew') }}:
            </b-col>
            <b-col sm="12" class="text-center">
              <ImageVector
                :filename="logoMskModal"
                :fallback="logoMskModalFallback"
                :alt="$t('redeem.msk.logo')"
                class="msk-logo"
              />
            </b-col>
            <b-col sm="12">
              <h5>{{ $t('redeem.msk.yourAdavantages') }}</h5>
              <ul class="wg-icon-ul">
                <li><b-icon-check />{{ $t('redeem.voucher.splitVoucher') }}</li>
                <li><b-icon-check />{{ $t('redeem.msk.allPartnersAvailable', { numberOfShops }) }}</li>
                <li><b-icon-check />{{ $t('redeem.msk.saveBalanceForYears') }}</li>
              </ul>
            </b-col>
          </b-col>
          <b-col md="6" sm="12">
            <b-col sm="12">
              <h5>{{ $t('redeem.msk.howItWorks') }}:</h5>
            </b-col>
            <b-col sm="12">
              <ul class="wg-icon-ul">
                <li><b-icon-check />{{ $t('redeem.msk.balanceManagement') }}</li>
                <li>
                  <b-icon-check />
                  <span class="text-uppercase"> {{ $t('wgs.std.optional').toUpperCase() }}: </span>
                  {{ $t('redeem.msk.canAddBalanceToAccount') }}
                </li>
                <li><b-icon-check /><span v-html="$t('redeem.msk.freeForver')" /></li>
              </ul>
            </b-col>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="6" class="mb-2">
            <b-button variant="primary" size="lg" block @click="redirectToMsk()">
              <div>{{ $t('wgs.std.yes') }},</div>
              <small>{{ $t('redeem.msk.createAccountAndRedeem', { price }) }}</small>
            </b-button>
          </b-col>
          <b-col cols="12" md="6" class="mb-2">
            <b-button variant="default" size="lg" block @click="$bvModal.hide('msk-promotion-modal')">
              <div>{{ $t('wgs.std.noThanks') }},</div>
              <small>{{ $t('redeem.msk.wantToRedeem', { price }) }}</small>
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BIconCheck } from 'bootstrap-vue';
import { mapGetters } from 'vuex';

import logoGiftCard from '~img/sites/wunschgutschein-de/WGS-Gutscheinkarte-ohne-Wertstufe.png';
import ImageVector from '~widget/ImageVector.vue';

export default {
  components: {
    BIconCheck,
    ImageVector,
  },

  props: {
    /* If true, the voucher info modal is visible */
    show: {
      type: Boolean,
    },
  },

  data() {
    return {
      showModal: false,
      step: 1,
    };
  },

  computed: {
    ...mapGetters({
      numberOfShops: 'getNumberOfShops',
      voucherValue: 'getVoucherValue',
      voucherCurrency: 'getVoucherCurrency',
    }),

    price() {
      return this.$n(this.voucherValue / 100, {
        key: 'currency',
        currency: this.voucherCurrency,
      });
    },

    stepOne() {
      return this.step === 1;
    },

    stepTwo() {
      return this.step === 2;
    },

    logoMskModal: () => 'logos/logo-msk.svg',

    logoMskModalFallback: () => 'logos/logo-msk.png',

    logoGiftCard: () => logoGiftCard,
  },

  created() {
    setTimeout(() => {
      this.showModal = this.show;
    }, 800);
  },

  methods: {
    redirectToMsk() {
      window.open('https://www.shoppingkonto.de/transaction/employeevoucher/');
    },
  },
};
</script>

<style lang="scss" scoped>
.now-new {
  margin-bottom: -20px;

  font-size: 1.5em;
}

ul {
  padding-left: 2em;

  list-style-type: none;

  li {
    position: relative;

    .bi-check {
      position: absolute;
      top: 0.08em;
      left: -1em;

      width: 1em;

      font-size: 180%;
      color: $brand-color-action;
      text-align: center;
    }
  }
}

/* MeinShoppingKonto (MSK) Logo
-------------------------------------- */
.msk-logo {
  width: 85%;
  max-width: 225px;
}
</style>
